import { useState, useCallback, useRef } from 'react';

export enum LoadingStatus {
    Idle = 'idle',
    Loading = 'loading',
    Success = 'success',
    Error = 'error'
}

export interface ProgressInfo {
    current: number;
    total: number;
    percentage: number;
}

export interface LoadingState {
    status: LoadingStatus;
    error: Error | null;
    message: string;
    operation: string;
    progress?: ProgressInfo;
    startTime?: number;
    estimatedTimeRemaining?: number;
}

const DEFAULT_STATE: LoadingState = {
    status: LoadingStatus.Idle,
    error: null,
    message: '',
    operation: '',
};

export function useLoadingState(initialMessage = '') {
    const [state, setState] = useState<LoadingState>({
        ...DEFAULT_STATE,
        message: initialMessage,
    });
    
    const progressInterval = useRef<NodeJS.Timeout>();
    const operationStartTime = useRef<number>();

    const startLoading = useCallback((message = '', operation = 'Loading') => {
        operationStartTime.current = Date.now();
        setState({
            status: LoadingStatus.Loading,
            error: null,
            message,
            operation,
            startTime: operationStartTime.current,
        });
    }, []);

    const updateProgress = useCallback((current: number, total: number) => {
        const percentage = Math.round((current / total) * 100);
        const elapsedTime = Date.now() - (operationStartTime.current || Date.now());
        const estimatedTotalTime = (elapsedTime * total) / current;
        const estimatedTimeRemaining = Math.max(0, estimatedTotalTime - elapsedTime);

        setState(prev => ({
            ...prev,
            progress: {
                current,
                total,
                percentage,
            },
            estimatedTimeRemaining,
        }));
    }, []);

    const stopLoading = useCallback((successMessage = '') => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }

        setState({
            status: LoadingStatus.Success,
            error: null,
            message: successMessage,
            operation: '',
            progress: undefined,
            estimatedTimeRemaining: undefined,
        });

        // Reset to idle state after showing success message
        setTimeout(() => {
            setState(DEFAULT_STATE);
        }, 2000);
    }, []);

    const setError = useCallback((error: Error, operation = '') => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }

        setState({
            status: LoadingStatus.Error,
            error,
            message: error.message,
            operation,
            progress: undefined,
            estimatedTimeRemaining: undefined,
        });
    }, []);

    const reset = useCallback(() => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }
        setState(DEFAULT_STATE);
    }, []);

    return {
        ...state,
        isLoading: state.status === LoadingStatus.Loading,
        isSuccess: state.status === LoadingStatus.Success,
        isError: state.status === LoadingStatus.Error,
        startLoading,
        stopLoading,
        setError,
        updateProgress,
        reset,
    };
}
